import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { ReferralMilestone } from '@/types/referral';

const BUCKET = 'referral-milestone-images';

export interface MilestoneNudgeConfig {
  enabled: boolean;
  within: number;
  subject: string;
  content: string;
}

export interface MilestoneCelebrationConfig {
  enabled: boolean;
  subject: string;
  content: string;
}

interface MilestoneTiersEditorProps {
  milestones: ReferralMilestone[];
  onChange: (milestones: ReferralMilestone[]) => void;
  nudgeConfig?: MilestoneNudgeConfig;
  onNudgeConfigChange?: (config: MilestoneNudgeConfig) => void;
  celebrationConfig?: MilestoneCelebrationConfig;
  onCelebrationConfigChange?: (config: MilestoneCelebrationConfig) => void;
  /** Brand ID for upload path (required for upload) */
  brandId?: string | null;
  /** Program ID for upload path (optional; when creating, use 'pending') */
  programId?: string | null;
  disabled?: boolean;
}

function sortMilestones(ms: ReferralMilestone[]): ReferralMilestone[] {
  return [...ms].sort((a, b) => a.threshold - b.threshold);
}

export function MilestoneTiersEditor({ milestones, onChange, nudgeConfig, onNudgeConfigChange, celebrationConfig, onCelebrationConfigChange, brandId, programId, disabled }: MilestoneTiersEditorProps) {
  const { toast } = useToast();
  const uploadRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const addTier = () => {
    const maxThreshold = milestones.length > 0 ? Math.max(...milestones.map((m) => m.threshold)) : 0;
    onChange(
      sortMilestones([
        ...milestones,
        { threshold: maxThreshold + 1, name: '', image: null },
      ])
    );
  };

  const updateTier = (index: number, updates: Partial<ReferralMilestone>) => {
    const next = [...milestones];
    next[index] = { ...next[index], ...updates };
    onChange(sortMilestones(next));
  };

  const removeTier = (index: number) => {
    onChange(milestones.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (index: number, file: File) => {
    if (!brandId) {
      toast({ title: 'Upload failed', description: 'No brand selected.', variant: 'destructive' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image (JPEG, PNG, WebP, GIF).', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum size is 5MB.', variant: 'destructive' });
      return;
    }
    const pathPrefix = programId ? `${brandId}/${programId}` : `${brandId}/pending`;
    const ext = file.name.split('.').pop() || 'png';
    const fileName = `${pathPrefix}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage.from(BUCKET).upload(fileName, file, { upsert: false });
    if (error) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
      return;
    }
    const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
    updateTier(index, { image: publicUrl });
  };

  const validateThresholds = (): string | null => {
    const thresholds = milestones.map((m) => m.threshold);
    const unique = new Set(thresholds);
    if (thresholds.some((t) => t <= 0)) return 'Thresholds must be positive';
    if (unique.size !== thresholds.length) return 'Thresholds must be unique';
    return null;
  };

  const validationError = validateThresholds();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Milestone tiers</Label>
        <Button type="button" variant="outline" size="sm" onClick={addTier} disabled={disabled}>
          <Plus className="h-4 w-4 mr-1" />
          Add tier
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Define reward tiers: invite X people to unlock each gift.
      </p>
      {validationError && (
        <p className="text-sm text-destructive">{validationError}</p>
      )}
      <div className="space-y-3 max-h-[280px] overflow-y-auto">
        {milestones.map((m, index) => (
          <div
            key={index}
            className="flex flex-col sm:flex-row gap-2 p-3 rounded-lg border bg-muted/30"
          >
            <div className="flex-1 grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Invites</Label>
                <Input
                  type="number"
                  min={1}
                  value={m.threshold || ''}
                  onChange={(e) =>
                    updateTier(index, { threshold: parseInt(e.target.value, 10) || 0 })
                  }
                  placeholder="5"
                  disabled={disabled}
                  className="mt-0.5"
                />
              </div>
              <div>
                <Label className="text-xs">Gift name</Label>
                <Input
                  value={m.name || ''}
                  onChange={(e) => updateTier(index, { name: e.target.value })}
                  placeholder="Free t-shirt"
                  disabled={disabled}
                  className="mt-0.5"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Image (optional)</Label>
                <div className="flex gap-2 items-center mt-0.5">
                  {m.image ? (
                    <>
                      <img
                        src={m.image}
                        alt=""
                        className="h-12 w-12 object-cover rounded border"
                      />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={m.image}
                          onChange={(e) =>
                            updateTier(index, { image: e.target.value.trim() || null })
                          }
                          placeholder="URL or upload"
                          disabled={disabled}
                          className="text-xs"
                        />
                      </div>
                      {!disabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => updateTier(index, { image: null })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        ref={(el) => { uploadRefs.current[index] = el; }}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleImageUpload(index, f);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={disabled || !brandId}
                        onClick={() => uploadRefs.current[index]?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                      <Input
                        placeholder="Or paste URL"
                        disabled={disabled}
                        className="flex-1 text-xs"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && (v.startsWith('http') || v.startsWith('/'))) {
                            updateTier(index, { image: v });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const v = (e.target as HTMLInputElement).value.trim();
                            if (v && (v.startsWith('http') || v.startsWith('/'))) {
                              updateTier(index, { image: v });
                            }
                          }
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeTier(index)}
                disabled={disabled}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {onNudgeConfigChange && nudgeConfig && (
        <div className="mt-6 pt-4 border-t space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="nudge-enabled"
              checked={nudgeConfig.enabled}
              onCheckedChange={(c) => onNudgeConfigChange({ ...nudgeConfig, enabled: !!c })}
              disabled={disabled}
            />
            <Label htmlFor="nudge-enabled" className="font-medium">
              Send reinforcement email when close to next tier
            </Label>
          </div>
          {nudgeConfig.enabled && (
            <>
              <div>
                <Label className="text-xs">Send when X invites away from next tier</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={nudgeConfig.within}
                  onChange={(e) =>
                    onNudgeConfigChange({ ...nudgeConfig, within: parseInt(e.target.value, 10) || 2 })
                  }
                  disabled={disabled}
                  className="mt-0.5"
                />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={nudgeConfig.subject}
                  onChange={(e) => onNudgeConfigChange({ ...nudgeConfig, subject: e.target.value })}
                  placeholder="You're {invites_left} invites away from {next_reward}!"
                  disabled={disabled}
                  className="mt-0.5"
                />
              </div>
              <div>
                <Label className="text-xs">Content</Label>
                <Textarea
                  value={nudgeConfig.content}
                  onChange={(e) => onNudgeConfigChange({ ...nudgeConfig, content: e.target.value })}
                  placeholder="Hi {first_name},\n\nYou're almost there! Just {invites_left} more invite(s) to unlock {next_reward}.\n\nShare your link: {referral_link}"
                  disabled={disabled}
                  className="mt-0.5"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variables: {'{first_name}'}, {'{invites_left}'}, {'{next_reward}'}, {'{referral_link}'}, {'{referral_count}'}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {onCelebrationConfigChange && celebrationConfig && (
        <div className="mt-6 pt-4 border-t space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="celebration-enabled"
              checked={celebrationConfig.enabled}
              onCheckedChange={(c) => onCelebrationConfigChange({ ...celebrationConfig, enabled: !!c })}
              disabled={disabled}
            />
            <Label htmlFor="celebration-enabled" className="font-medium">
              Send celebration email when referrer crosses a tier
            </Label>
          </div>
          {celebrationConfig.enabled && (
            <>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={celebrationConfig.subject}
                  onChange={(e) => onCelebrationConfigChange({ ...celebrationConfig, subject: e.target.value })}
                  placeholder="Congratulations! You've unlocked {reward}!"
                  disabled={disabled}
                  className="mt-0.5"
                />
              </div>
              <div>
                <Label className="text-xs">Content</Label>
                <Textarea
                  value={celebrationConfig.content}
                  onChange={(e) => onCelebrationConfigChange({ ...celebrationConfig, content: e.target.value })}
                  placeholder="Hi {first_name},\n\nCongratulations! You've reached {threshold} invites and unlocked {reward}!\n\nKeep sharing: {referral_link}"
                  disabled={disabled}
                  className="mt-0.5"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variables: {'{first_name}'}, {'{threshold}'}, {'{reward}'}, {'{referral_count}'}, {'{referral_link}'}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
